import { Directive, input } from '@angular/core';
import { select } from '@ngxs/store';
import { SupportTimeOptions } from 'ng-zorro-antd/date-picker';
import { LocalizationState } from '../state/localization.state';
import { BaseLocalizedFormatDirective } from './base-localized-format.directive';

/**
 * `wilsonUseLocalizedDateFormat` directive
 *
 * Centralizes `nzFormat` for NG-ZORRO `nz-date-picker` / `nz-range-picker`
 * using `LocalizationState` (so formats always match the active locale).
 *
 * Usage (add the attribute to the picker):
 * ```html
 * <!-- Date-only (nzMode defaults to "date") -->
 * <nz-date-picker wilsonUseLocalizedDateFormat></nz-date-picker>
 *
 * <!-- Date picker explicitly in "date" mode -->
 * <nz-date-picker nzMode="date" wilsonUseLocalizedDateFormat></nz-date-picker>
 *
 * <!-- When showing time, the directive sets the time format for you -->
 * <nz-date-picker nzMode="date" wilsonUseLocalizedDateFormat [nzShowTime]="true"></nz-date-picker>
 * ```
 *
 * Constraints:
 * - Only supports `nzMode="date"` (or when `nzMode` is omitted). It throws at
 *   runtime for other modes.
 * - Do not set `nzFormat` manually when using this directive.
 * - If you set `nzShowTime` as an object containing `nzFormat`, remove it
 *   and let the directive apply `nzShowTime.nzFormat`.
 *
 * Notes:
 * - This directive sets `nzFormat` based on:
 *   - `LocalizationState.getLocalizedDateFormat` when `nzShowTime` is false
 *   - `LocalizationState.getLocalizedDateTimeFormat` when `nzShowTime` is true
 * - `wilsonUseLocalizedDateFormat` is enforced via a custom ESLint rule
 *   (currently added as `warn` in this repo; it may become `error` later).
 */
@Directive({
  selector:
    'nz-date-picker[wilsonUseLocalizedDateFormat], nz-range-picker[wilsonUseLocalizedDateFormat]',
  standalone: true,
})
export class UseLocalizedDateFormatDirective extends BaseLocalizedFormatDirective {
  protected readonly directiveName = 'wilsonUseLocalizedDateFormat';

  readonly nzShowTime = input<boolean | SupportTimeOptions>();

  private readonly localizedDateFormat = select(
    LocalizationState.getLocalizedDateFormat,
  );

  private readonly localizedDateTimeFormat = select(
    LocalizationState.getLocalizedDateTimeFormat,
  );

  private readonly localizedTimeFormat = select(
    LocalizationState.getLocalizedTimeFormat,
  );

  private hasThrownForManualNzShowTimeNzFormat = false;
  private shouldThrowForManualNzShowTimeNzFormat = false;

  protected override checkMode(mode?: string): void {
    // Only apply to day-grid date pickers.
    // Default nzMode in ng-zorro is 'date', so this is mainly a safeguard.
    if (mode && mode !== 'date') {
      throw new Error(
        'wilsonUseLocalizedDateFormat can only be used on nz-date-picker with nzMode="date"',
      );
    }
  }

  protected override applyCustomFormatEffects(): void {
    // Fall back to `picker.nzShowTime` when the directive input is unset.
    const directiveNzShowTime = this.nzShowTime();
    const nzShowTime =
      // eslint-disable-next-line no-undefined
      directiveNzShowTime === undefined
        ? this.picker.nzShowTime
        : directiveNzShowTime;

    this.shouldThrowForManualNzShowTimeNzFormat =
      !!nzShowTime &&
      typeof nzShowTime === 'object' &&
      'nzFormat' in nzShowTime &&
      !this.hasThrownForManualNzShowTimeNzFormat;

    if (nzShowTime == true) {
      // ng-zorro accepts `SupportTimeOptions` (object) or boolean for nzShowTime.
      // We centralize `nzFormat` via LocalizationState.
      this.picker.nzShowTime = { nzFormat: this.localizedTimeFormat() };
    } else if (nzShowTime && typeof nzShowTime === 'object') {
      // If the consumer provided time options, we still apply our centralized
      // time format, but we keep any other time options intact.
      this.picker.nzShowTime = {
        ...nzShowTime,
        nzFormat: this.localizedTimeFormat(),
      };
    }

    // nzShowTime=false => use localized date-only format
    // nzShowTime=true or truthy => use localized date+time format
    const format = nzShowTime
      ? this.localizedDateTimeFormat()
      : this.localizedDateFormat();
    this.picker.nzFormat = format;
  }

  protected override throwAdditionalErrors(): void {
    if (this.shouldThrowForManualNzShowTimeNzFormat) {
      this.hasThrownForManualNzShowTimeNzFormat = true;
      throw new Error(
        'Do not set nzShowTime.nzFormat manually on nz-date-picker/nz-range-picker when using wilsonUseLocalizedDateFormat. Remove nzShowTime.nzFormat and let the directive apply it.',
      );
    }
  }
}
